import { AnimatePresence } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import { useAssistantChat } from '../../../api/getChat';
import { useProfile } from '../../../api/queries/useProfile';
import { useChatStore } from '../../../stores/chatStore';
import { SuggestedQuestions } from '../SuggestedQuestions/SuggestedQuestions';
import { Avatar } from '../avatar/avatar';

import { ChatMessageBubble } from './message';
import styles from './styles.module.css';

export const MessageList = () => {
  const messages = useChatStore.use.messages();
  const isLoading = useChatStore.use.loading();
  const isOnboarded = useChatStore.use.isOnboarded();

  const [avatarIndex, setAvatarIndex] = useState<number | null>(null);
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const targetMsgRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);

  const { data: profile } = useProfile();

  const { mutate: sendMessage, isIdle } = useAssistantChat();

  useEffect(() => {
    if (messages.length === 0 && isIdle && isOnboarded) {
      sendMessage({
        message: profile?.result ? `Hoi, ik ben ${profile?.result?.greetingName}` : 'Hoi!',
      });
    }
  }, [messages, isIdle, sendMessage, profile]);

  useEffect(() => {
    const currentMessageCount = messages.length;
    const lastMessage = messages[messages.length - 1];

    // If a new message was added
    if (currentMessageCount > previousMessageCount) {
      if (lastMessage?.type === 'user') {
        // User message sent - scroll to bottom
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }

    // If loading just stopped and we have a new AI message
    if (!isLoading && lastMessage?.type === 'ai') {
      // AI message received - scroll to align AI message top with container top
      // This happens immediately when the message is received, so users can see the typewriter effect
      setTimeout(() => {
        if (targetMsgRef.current && containerRef.current) {
          const messageRect = targetMsgRef.current.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();
          const scrollTop = containerRef.current.scrollTop;

          // Calculate how much to scroll to align message top with container top
          const targetScrollTop = scrollTop + (messageRect.top - containerRect.top);

          containerRef.current.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth',
          });
        }
      }, 200);
    }

    if (messages.filter((m) => m.type === 'ai').length === 0) {
      setAvatarIndex(null);
    } else {
      setAvatarIndex(messages.length - 1);
    }

    setPreviousMessageCount(currentMessageCount);
  }, [messages, isLoading, previousMessageCount]);

  useEffect(() => {
    setTimeout(() => {
      if (avatarIndex === null) return;
      const container = containerRef.current;
      const avatar = avatarRef.current;
      if (!container || !avatar) return;

      if (!targetMsgRef.current) return;

      const msgRect = targetMsgRef.current.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const scroll = container.scrollTop;
      const scrollHeight = container.scrollHeight;

      let avatarHeight;
      if (scrollHeight <= containerRect.height) {
        avatarHeight = (containerRect.height - msgRect.y + 25) * -1;
      } else {
        avatarHeight = msgRect.y + scroll - containerRect.height - 25;
      }

      avatar.style.transform = `translateY(${avatarHeight}px)`;
    }, 200);
  }, [avatarIndex]);

  return (
    <div ref={containerRef} className={styles.wrapper}>
      <AnimatePresence key='message-list-animation-container'>
        {messages.length > 0 && avatarIndex !== null && (
          <Avatar
            src='https://media.insify.com/myinsify/assistant/ai-carmi.webm'
            alt='AI Assistant'
            key='avatar'
            ref={avatarRef}
          />
        )}
        <div
          key='message-list'
          style={{ paddingBottom: 4, paddingLeft: 50, gap: 8, display: 'flex', flexDirection: 'column' }}
        >
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1;
            const isAIMessage = message.type === 'ai';
            const shouldAttachRef = isLastMessage && isAIMessage;

            return (
              <ChatMessageBubble
                ref={shouldAttachRef ? targetMsgRef : undefined}
                message={message}
                key={message.id}
                isLastMessage={isLastMessage}
              />
            );
          })}

          {/* Show loading bubble if the last message is from the AI and isLoading is true */}
          {isLoading && <ChatMessageBubble key='loading-bubble' />}
          <SuggestedQuestions key='suggested-questions' />
        </div>
      </AnimatePresence>

      {/* <ShimmerText>What up this is shimmery text yo</ShimmerText> */}
      <div ref={messagesEndRef} />
    </div>
  );
};
